import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Warehouse as WarehouseIcon, Package, ArrowRightLeft, AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Wrench, ClipboardCheck, Link2, Link2Off } from "lucide-react";
import { Modal, In, Sel } from "./Projects";
import TransfersTab from "@/components/warehouse/TransfersTab";
import CustodiesTab from "@/components/warehouse/CustodiesTab";
import EvidencesTab from "@/components/warehouse/EvidencesTab";

const PRODUCT_UNITS = [
  { value: "pza", label: "pza" }, { value: "kg", label: "kg" }, { value: "ton", label: "ton" },
  { value: "m", label: "m" }, { value: "m2", label: "m²" }, { value: "m3", label: "m³" },
  { value: "ml", label: "ml" }, { value: "sac", label: "saco" }, { value: "lt", label: "lt" }, { value: "lote", label: "lote" },
];
const ITEM_TYPES = [
  { value: "consumible", label: "Consumible" },
  { value: "herramienta", label: "Herramienta" },
  { value: "equipo", label: "Equipo reutilizable" },
];
const MOV_COLORS = {
  entrada: ["var(--green)", "rgba(22,163,74,0.18)"],
  salida: ["var(--magenta)", "rgba(244,114,182,0.18)"],
  ajuste: ["var(--cyan)", "rgba(34,211,238,0.18)"],
  transferencia_salida: ["#eab308", "rgba(234,179,8,0.14)"],
  transferencia_entrada: ["#eab308", "rgba(234,179,8,0.14)"],
  resguardo: ["#f97316", "rgba(249,115,22,0.14)"],
  devolucion_resguardo: ["var(--green)", "rgba(22,163,74,0.12)"],
};
const MOV_LABELS = {
  transferencia_salida: "transf. salida", transferencia_entrada: "transf. entrada",
  resguardo: "resguardo", devolucion_resguardo: "devolución",
};

export default function Warehouses() {
  const { user } = useAuth();
  const [tab, setTab] = useState("stock");
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pendingEvidences, setPendingEvidences] = useState(0);
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);
  const canOperate = canManage || ["residente", "supervisor"].includes(user?.role);

  // forms
  const [openWh, setOpenWh] = useState(false);
  const [editWh, setEditWh] = useState(null);
  const [whForm, setWhForm] = useState({ name: "", wh_type: "general", project_id: "", address: "", manager_name: "", operator_name: "", notes: "", active: true });
  const [openPr, setOpenPr] = useState(false);
  const [editPr, setEditPr] = useState(null);
  const [prForm, setPrForm] = useState({ code: "", name: "", unit: "pza", item_type: "consumible", category: "", min_stock: 0 });
  const [openMov, setOpenMov] = useState(null); // 'entrada' | 'salida' | null
  const [movForm, setMovForm] = useState({ warehouse_id: "", product_id: "", quantity: "", unit_cost: 0, project_id: "", vendor_id: "", invoice_number: "", notes: "" });

  const load = async () => {
    const [w, p, s, v, pr, ev] = await Promise.all([
      api.get("/warehouses"), api.get("/products"),
      api.get("/stock", { params: filterWarehouse ? { warehouse_id: filterWarehouse } : {} }),
      api.get("/vendors").catch(() => ({ data: [] })), api.get("/projects"),
      canOperate ? api.get("/stock-evidences", { params: { status: "pendiente" } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setWarehouses(w.data); setProducts(p.data); setStock(s.data); setVendors(v.data); setProjects(pr.data);
    setPendingEvidences(ev.data.length);
  };
  const loadMovements = async () => {
    const { data } = await api.get("/stock-movements", { params: filterWarehouse ? { warehouse_id: filterWarehouse } : {} });
    setMovements(data);
  };
  useEffect(() => { load(); loadMovements(); /* eslint-disable-next-line */ }, [filterWarehouse]);
  const reloadAll = () => { load(); loadMovements(); };

  // ----- Warehouse CRUD
  const saveWh = async (e) => {
    e.preventDefault();
    const payload = { ...whForm };
    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    try {
      if (editWh) await api.patch(`/warehouses/${editWh}`, payload);
      else await api.post("/warehouses", payload);
      setOpenWh(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const removeWh = async (id) => { if (!window.confirm("¿Eliminar almacén?")) return; try { await api.delete(`/warehouses/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  const copyEvidenceLink = async (w) => {
    try {
      const { data } = await api.post(`/warehouses/${w.id}/evidence-link`);
      const url = `${window.location.origin}${data.path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado. Compártelo con el encargado para que registre entradas/salidas con fotos (sin acceso financiero).", { duration: 6000 });
      } catch {
        window.prompt("Copia este link para el encargado de la bodega:", url);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error al generar el link"); }
  };
  const revokeEvidenceLink = async (w) => {
    if (!window.confirm(`¿Revocar el link de evidencias de ${w.name}? El formulario dejará de funcionar.`)) return;
    try { await api.delete(`/warehouses/${w.id}/evidence-link`); toast.success("Link revocado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // ----- Product CRUD
  const savePr = async (e) => {
    e.preventDefault();
    const payload = { ...prForm, min_stock: Number(prForm.min_stock || 0) };
    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    try {
      if (editPr) await api.patch(`/products/${editPr}`, payload);
      else await api.post("/products", payload);
      setOpenPr(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const removePr = async (id) => { if (!window.confirm("¿Eliminar producto?")) return; try { await api.delete(`/products/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  // ----- Movements
  const startMovement = (type) => {
    setOpenMov(type);
    setMovForm({
      warehouse_id: filterWarehouse || warehouses[0]?.id || "",
      product_id: products[0]?.id || "",
      quantity: "", unit_cost: 0,
      project_id: type === "salida" ? projects[0]?.id || "" : "",
      vendor_id: "", invoice_number: "", notes: "",
    });
  };
  const saveMovement = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        type: openMov,
        warehouse_id: movForm.warehouse_id,
        product_id: movForm.product_id,
        quantity: Number(movForm.quantity),
        unit_cost: openMov === "entrada" ? Number(movForm.unit_cost || 0) : 0,
        project_id: movForm.project_id || null,
        vendor_id: movForm.vendor_id || null,
        invoice_number: movForm.invoice_number || null,
        notes: movForm.notes || null,
      };
      Object.keys(payload).forEach((k) => (payload[k] === null || payload[k] === "") && delete payload[k]);
      await api.post("/stock-movements", payload);
      setOpenMov(null);
      reloadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const lowStock = stock.filter((s) => s.low_stock);
  const totalValue = stock.reduce((s, x) => s + x.total_value, 0);
  const totalCustody = stock.reduce((s, x) => s + (x.qty_custody || 0), 0);
  const projectLabel = (pid) => { const p = projects.find((x) => x.id === pid); return p ? `${p.code} · ${p.name}` : "—"; };

  return (
    <div data-testid="warehouses-page">
      <PageHeader subtitle="Inventario, transferencias y resguardos" title="Almacenes"
        actions={canOperate && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="new-entry-btn" onClick={() => startMovement("entrada")} disabled={!warehouses.length || !products.length} className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-50" style={{ borderColor: "var(--green)", color: "var(--green)" }}>
              <ArrowDownToLine className="w-4 h-4" /> Entrada
            </button>
            <button data-testid="new-exit-btn" onClick={() => startMovement("salida")} disabled={!warehouses.length || !products.length} className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-50" style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}>
              <ArrowUpFromLine className="w-4 h-4" /> Salida
            </button>
          </div>
        )} />

      <div className="px-4 lg:px-8 pt-4 flex gap-2 flex-wrap items-center">
        <TabBtn active={tab === "stock"} onClick={() => setTab("stock")} testId="tab-stock"><Package className="w-3.5 h-3.5" /> Stock</TabBtn>
        <TabBtn active={tab === "movements"} onClick={() => setTab("movements")} testId="tab-movements"><ArrowRightLeft className="w-3.5 h-3.5" /> Movimientos ({movements.length})</TabBtn>
        <TabBtn active={tab === "transfers"} onClick={() => setTab("transfers")} testId="tab-transfers"><ArrowRightLeft className="w-3.5 h-3.5" /> Transferencias</TabBtn>
        <TabBtn active={tab === "custodies"} onClick={() => setTab("custodies")} testId="tab-custodies"><Wrench className="w-3.5 h-3.5" /> Resguardos</TabBtn>
        <TabBtn active={tab === "evidences"} onClick={() => setTab("evidences")} testId="tab-evidences">
          <ClipboardCheck className="w-3.5 h-3.5" /> Evidencias
          {pendingEvidences > 0 && <span className="ml-1 px-1.5 rounded-full text-[10px] font-bold" style={{ background: "var(--red)", color: "#fff" }}>{pendingEvidences}</span>}
        </TabBtn>
        <TabBtn active={tab === "warehouses"} onClick={() => setTab("warehouses")} testId="tab-warehouses"><WarehouseIcon className="w-3.5 h-3.5" /> Almacenes ({warehouses.length})</TabBtn>
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} testId="tab-products"><Package className="w-3.5 h-3.5" /> Productos ({products.length})</TabBtn>

        <div className="ml-auto flex items-center gap-2">
          <span className="label-mono">Almacén:</span>
          <select data-testid="filter-warehouse" value={filterWarehouse} onChange={(e) => setFilterWarehouse(e.target.value)} className="input-cyber px-3 py-1.5 text-sm">
            <option value="" style={{ background: "#0a0f1d" }}>Todos</option>
            {warehouses.map((w) => <option key={w.id} value={w.id} style={{ background: "#0a0f1d" }}>{w.name}</option>)}
          </select>
        </div>
      </div>

      <div className="px-4 lg:px-8 py-6 space-y-4">
        {tab === "stock" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KpiCard label="Almacenes" value={warehouses.length} />
              <KpiCard label="SKUs" value={products.length} />
              <KpiCard label="Bajo mínimo" value={lowStock.length} color={lowStock.length ? "var(--red)" : "var(--green)"} />
              <KpiCard label="En resguardo" value={totalCustody} color="#f97316" />
              <KpiCard label="Valor inventario" value={fmtMXN(totalValue)} color="var(--cyan)" />
            </div>
            {lowStock.length > 0 && (
              <div className="panel p-3" style={{ borderColor: "var(--red)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4" style={{ color: "var(--red)" }} />
                  <span className="label-mono" style={{ color: "var(--red)" }}>// PRODUCTOS BAJO MÍNIMO</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {lowStock.map((s) => (
                    <div key={`${s.warehouse_id}-${s.product_id}`} className="text-xs flex items-center justify-between px-2 py-1 rounded-sm" style={{ background: "rgba(220,38,38,0.06)" }}>
                      <span><span className="font-semibold text-slate-100">{s.product_name}</span> · {s.warehouse_name}</span>
                      <span className="font-mono"><span style={{ color: "var(--red)" }}>{s.quantity}</span> / {s.min_stock} {s.product_unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="panel overflow-x-auto">
              <table className="w-full text-sm min-w-[1000px]" data-testid="stock-table">
                <thead className="panel-header"><tr className="text-left"><Th>Almacén</Th><Th>Código</Th><Th>Producto</Th><Th>Tipo</Th><Th right>Existencia</Th><Th right>Resguardo</Th><Th right>Mínimo</Th><Th right>Costo prom.</Th><Th right>Valor</Th></tr></thead>
                <tbody>
                  {stock.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-500">Sin existencias.</td></tr>}
                  {stock.map((s) => (
                    <tr key={`${s.warehouse_id}-${s.product_id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                      <Td className="text-slate-300">{s.warehouse_name}</Td>
                      <Td className="font-mono text-xs text-slate-400">{s.product_code}</Td>
                      <Td><span className="font-semibold text-slate-100">{s.product_name}</span></Td>
                      <Td className="text-[10px] uppercase text-slate-400">{s.item_type || "consumible"}</Td>
                      <Td right><span className="font-mono font-bold tabular-nums" style={{ color: s.low_stock ? "var(--red)" : "var(--cyan)" }}>{s.quantity}</span> <span className="text-xs text-slate-500">{s.product_unit}</span></Td>
                      <Td right className="font-mono text-xs" style={{ color: s.qty_custody ? "#f97316" : "inherit" }}>{s.qty_custody || 0}</Td>
                      <Td right className="font-mono text-xs text-slate-400">{s.min_stock}</Td>
                      <Td right className="font-mono text-slate-300">{fmtMXN(s.avg_unit_cost)}</Td>
                      <Td right className="font-mono font-semibold text-slate-100">{fmtMXN(s.total_value)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "movements" && (
          <div className="panel overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]" data-testid="movements-table">
              <thead className="panel-header"><tr className="text-left"><Th>Fecha</Th><Th>Tipo</Th><Th>Almacén</Th><Th>Producto</Th><Th right>Cant.</Th><Th>Proyecto</Th><Th>Proveedor</Th><Th right>Costo</Th><Th>Por</Th></tr></thead>
              <tbody>
                {movements.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-500">Sin movimientos.</td></tr>}
                {movements.map((m) => {
                  const [color, bg] = MOV_COLORS[m.type] || ["var(--cyan)", "rgba(34,211,238,0.18)"];
                  return (
                    <tr key={m.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                      <Td className="font-mono text-xs">{fmtDate(m.created_at)}</Td>
                      <Td><span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm whitespace-nowrap" style={{ background: bg, color }}>{MOV_LABELS[m.type] || m.type}</span></Td>
                      <Td className="text-slate-300">{m.warehouse_name}</Td>
                      <Td className="text-slate-100 font-semibold">{m.product_name}</Td>
                      <Td right className="font-mono">{m.quantity} {m.product_unit}</Td>
                      <Td className="text-xs text-slate-400">{m.project_label || "—"}</Td>
                      <Td className="text-xs text-slate-400">{m.vendor_name || "—"}</Td>
                      <Td right className="font-mono">{fmtMXN(m.total_cost)}</Td>
                      <Td className="text-xs text-slate-400">{m.created_by_name || "—"}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "transfers" && <TransfersTab warehouses={warehouses} products={products} canManage={canManage} onChanged={reloadAll} />}
        {tab === "custodies" && <CustodiesTab warehouses={warehouses} products={products} projects={projects} onChanged={reloadAll} />}
        {tab === "evidences" && <EvidencesTab onChanged={reloadAll} />}

        {tab === "warehouses" && (
          <div className="panel overflow-x-auto">
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
              <span className="label-mono" style={{ color: "var(--cyan)" }}>// LISTA DE ALMACENES</span>
              {canManage && <button data-testid="new-warehouse-btn" onClick={() => { setEditWh(null); setWhForm({ name: "", wh_type: "general", project_id: "", address: "", manager_name: "", operator_name: "", notes: "", active: true }); setOpenWh(true); }} className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"><Plus className="w-3 h-3" /> Almacén</button>}
            </div>
            <table className="w-full text-sm min-w-[900px]" data-testid="warehouses-table">
              <thead className="panel-header"><tr className="text-left"><Th>Nombre</Th><Th>Tipo</Th><Th>Obra</Th><Th>Responsable</Th><Th>Encargado</Th><Th>Activo</Th><Th right>Acciones</Th></tr></thead>
              <tbody>
                {warehouses.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-500">Sin almacenes aún.</td></tr>}
                {warehouses.map((w) => (
                  <tr key={w.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                    <Td><span className="font-semibold text-slate-100">{w.name}</span>{w.address && <div className="text-[11px] text-slate-500">{w.address}</div>}</Td>
                    <Td><span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm" style={{ border: `1px solid ${w.wh_type === "obra" ? "var(--magenta)" : "var(--cyan)"}`, color: w.wh_type === "obra" ? "var(--magenta)" : "var(--cyan)" }}>{w.wh_type === "obra" ? "BODEGA DE OBRA" : "GENERAL"}</span></Td>
                    <Td className="text-xs text-slate-300">{w.project_id ? projectLabel(w.project_id) : "—"}</Td>
                    <Td className="text-xs text-slate-300">{w.responsible_name || w.manager_name || "—"}</Td>
                    <Td className="text-xs text-slate-300">{w.operator_name || "—"}</Td>
                    <Td>{w.active ? <span className="text-xs font-semibold" style={{ color: "var(--green)" }}>SÍ</span> : <span className="text-slate-500 text-xs">NO</span>}</Td>
                    <Td right>
                      <button data-testid={`evidence-link-${w.id}`} onClick={() => copyEvidenceLink(w)} title="Link de evidencias para encargado" className="p-1 rounded-sm hover:bg-white/5 mr-1" style={{ color: "var(--green)" }}><Link2 className="w-4 h-4" /></button>
                      {w.evidence_token && <button data-testid={`revoke-link-${w.id}`} onClick={() => revokeEvidenceLink(w)} title="Revocar link de evidencias" className="p-1 rounded-sm hover:bg-white/5 mr-2" style={{ color: "var(--red)" }}><Link2Off className="w-4 h-4" /></button>}
                      {canManage && <button data-testid={`edit-wh-${w.id}`} onClick={() => { setEditWh(w.id); setWhForm({ name: w.name, wh_type: w.wh_type || "general", project_id: w.project_id || "", address: w.address || "", manager_name: w.manager_name || "", operator_name: w.operator_name || "", notes: w.notes || "", active: w.active }); setOpenWh(true); }} className="text-xs font-semibold hover:underline mr-3" style={{ color: "var(--cyan)" }}>EDITAR</button>}
                      {user?.role === "admin" && <button data-testid={`delete-wh-${w.id}`} onClick={() => removeWh(w.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "products" && (
          <div className="panel overflow-x-auto">
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
              <span className="label-mono" style={{ color: "var(--cyan)" }}>// CATÁLOGO DE PRODUCTOS</span>
              {canManage && <button data-testid="new-product-btn" onClick={() => { setEditPr(null); setPrForm({ code: "", name: "", unit: "pza", item_type: "consumible", category: "", min_stock: 0 }); setOpenPr(true); }} className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"><Plus className="w-3 h-3" /> Producto</button>}
            </div>
            <table className="w-full text-sm min-w-[750px]" data-testid="products-table">
              <thead className="panel-header"><tr className="text-left"><Th>Código</Th><Th>Nombre</Th><Th>Tipo</Th><Th>Unidad</Th><Th>Categoría</Th><Th right>Mín.</Th><Th right>Acciones</Th></tr></thead>
              <tbody>
                {products.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-500">Sin productos aún.</td></tr>}
                {products.map((p) => (
                  <tr key={p.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                    <Td className="font-mono text-xs">{p.code}</Td>
                    <Td><span className="font-semibold text-slate-100">{p.name}</span></Td>
                    <Td className="text-[10px] uppercase text-slate-400">{p.item_type || "consumible"}</Td>
                    <Td className="text-xs text-slate-300">{p.unit}</Td>
                    <Td className="text-xs text-slate-300">{p.category || "—"}</Td>
                    <Td right className="font-mono">{p.min_stock}</Td>
                    <Td right>
                      {canManage && <button data-testid={`edit-pr-${p.id}`} onClick={() => { setEditPr(p.id); setPrForm({ code: p.code, name: p.name, unit: p.unit, item_type: p.item_type || "consumible", category: p.category || "", min_stock: p.min_stock }); setOpenPr(true); }} className="text-xs font-semibold hover:underline mr-3" style={{ color: "var(--cyan)" }}>EDITAR</button>}
                      {user?.role === "admin" && <button data-testid={`delete-pr-${p.id}`} onClick={() => removePr(p.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Warehouse modal */}
      {openWh && (
        <Modal onClose={() => setOpenWh(false)} title={editWh ? "Editar almacén" : "Nuevo almacén"}>
          <form onSubmit={saveWh} className="space-y-4">
            <In label="Nombre" testId="wh-name" value={whForm.name} onChange={(v) => setWhForm({ ...whForm, name: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Tipo" testId="wh-type" value={whForm.wh_type} onChange={(v) => setWhForm({ ...whForm, wh_type: v })} options={[{ value: "general", label: "Almacén general" }, { value: "obra", label: "Bodega de obra" }]} />
              <Sel label={whForm.wh_type === "obra" ? "Obra (obligatoria)" : "Obra (opcional)"} testId="wh-project" value={whForm.project_id} onChange={(v) => setWhForm({ ...whForm, project_id: v })} options={[{ value: "", label: "—" }, ...projects.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))]} />
            </div>
            {whForm.wh_type === "obra" && <p className="text-xs text-slate-400">El residente de la obra queda como responsable formal del almacén.</p>}
            <In label="Dirección" testId="wh-address" value={whForm.address} onChange={(v) => setWhForm({ ...whForm, address: v })} required={false} />
            <div className="grid grid-cols-2 gap-3">
              <In label="Encargado operativo" testId="wh-operator" value={whForm.operator_name} onChange={(v) => setWhForm({ ...whForm, operator_name: v })} required={false} />
              <In label="Encargado (general)" testId="wh-manager" value={whForm.manager_name} onChange={(v) => setWhForm({ ...whForm, manager_name: v })} required={false} />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={whForm.notes} onChange={(e) => setWhForm({ ...whForm, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenWh(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-wh-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Product modal */}
      {openPr && (
        <Modal onClose={() => setOpenPr(false)} title={editPr ? "Editar producto" : "Nuevo producto"}>
          <form onSubmit={savePr} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <In label="Código" testId="pr-code" value={prForm.code} onChange={(v) => setPrForm({ ...prForm, code: v })} />
              <Sel label="Unidad" testId="pr-unit" value={prForm.unit} onChange={(v) => setPrForm({ ...prForm, unit: v })} options={PRODUCT_UNITS} />
            </div>
            <In label="Nombre" testId="pr-name" value={prForm.name} onChange={(v) => setPrForm({ ...prForm, name: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Clasificación" testId="pr-item-type" value={prForm.item_type} onChange={(v) => setPrForm({ ...prForm, item_type: v })} options={ITEM_TYPES} />
              <In label="Categoría" testId="pr-category" value={prForm.category} onChange={(v) => setPrForm({ ...prForm, category: v })} required={false} />
            </div>
            <In label="Stock mínimo" testId="pr-min" type="number" value={prForm.min_stock} onChange={(v) => setPrForm({ ...prForm, min_stock: v })} required={false} />
            <p className="text-xs text-slate-400">Herramientas y equipos se pueden entregar en resguardo y deben devolverse; los consumibles salen por consumo.</p>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenPr(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-pr-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Movement modal */}
      {openMov && (
        <Modal onClose={() => setOpenMov(null)} title={openMov === "entrada" ? "Entrada de mercancía" : "Salida a obra"}>
          <form onSubmit={saveMovement} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Almacén" testId="mov-warehouse" value={movForm.warehouse_id} onChange={(v) => setMovForm({ ...movForm, warehouse_id: v })} options={warehouses.map((w) => ({ value: w.id, label: w.name }))} />
              <Sel label="Producto" testId="mov-product" value={movForm.product_id} onChange={(v) => setMovForm({ ...movForm, product_id: v })} options={products.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label={`Cantidad (${products.find((p) => p.id === movForm.product_id)?.unit || ""})`} testId="mov-quantity" type="number" value={movForm.quantity} onChange={(v) => setMovForm({ ...movForm, quantity: v })} />
              {openMov === "entrada" && <In label="Costo unitario MXN" testId="mov-unit-cost" type="number" value={movForm.unit_cost} onChange={(v) => setMovForm({ ...movForm, unit_cost: v })} required={false} />}
              {openMov === "salida" && <Sel label="Proyecto destino" testId="mov-project" value={movForm.project_id} onChange={(v) => setMovForm({ ...movForm, project_id: v })} options={projects.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))} />}
            </div>
            {openMov === "entrada" && (
              <div className="grid grid-cols-2 gap-3">
                <Sel label="Proveedor" testId="mov-vendor" value={movForm.vendor_id} onChange={(v) => setMovForm({ ...movForm, vendor_id: v })} options={[{ value: "", label: "—" }, ...vendors.map((vv) => ({ value: vv.id, label: vv.name }))]} />
                <In label="Folio factura" testId="mov-invoice" value={movForm.invoice_number} onChange={(v) => setMovForm({ ...movForm, invoice_number: v })} required={false} />
              </div>
            )}
            <div className="px-3 py-2 rounded-sm text-sm" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
              {openMov === "entrada" ? (
                <>Costo total: <span className="font-bold font-mono" style={{ color: "var(--green)" }}>{fmtMXN((Number(movForm.quantity) || 0) * (Number(movForm.unit_cost) || 0))}</span></>
              ) : (
                <>Se generará un gasto automático <span className="text-slate-400">(materiales)</span> en el proyecto al costo promedio actual del almacén.</>
              )}
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={movForm.notes} onChange={(e) => setMovForm({ ...movForm, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenMov(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-mov-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Confirmar {openMov}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const Th = ({ children, right }) => <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, className = "", style }) => <td style={style} className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>;
const TabBtn = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"
    style={{ background: active ? "rgba(34,211,238,0.12)" : "transparent", color: active ? "var(--cyan)" : "var(--text-dim)", border: `1px solid ${active ? "var(--cyan)" : "var(--border)"}` }}>
    {children}
  </button>
);
const KpiCard = ({ label, value, color }) => (
  <div className="panel p-3">
    <div className="label-mono">{label}</div>
    <div className="font-display font-black text-2xl tabular-nums" style={{ color: color || "var(--text)" }}>{value}</div>
  </div>
);
